import type { IdentitySearchRow } from '../../../../../data-layer/licenseManagementApi';

const TIERS = [
  'Advanced User',
  'Core User',
  'Self-Service User',
  'Developer User',
] as const;

const STATUSES = ['Compliant', 'Over-licensed', 'Dormant'] as const;

const DEPARTMENTS = [
  'Engineering',
  'Finance',
  'HR',
  'Sales',
  'Marketing',
  'Legal',
  'Operations',
  'IT',
  'Product',
  'Security',
] as const;

function tier(i: number) {
  return TIERS[i % TIERS.length];
}

function status(i: number) {
  return STATUSES[i % STATUSES.length];
}

function dept(i: number) {
  return DEPARTMENTS[i % DEPARTMENTS.length];
}

function lastActive(daysAgo: number) {
  const d = new Date('2026-08-06');

  d.setDate(d.getDate() - daysAgo);

  return d.toISOString().split('T')[0];
}

const humanNames = [
  'alice.johnson',
  'bob.smith',
  'carol.white',
  'david.brown',
  'eve.davis',
  'frank.miller',
  'grace.wilson',
  'henry.moore',
  'iris.taylor',
  'jack.anderson',
  'karen.thomas',
  'leo.jackson',
  'mia.harris',
  'noah.martin',
  'olivia.garcia',
  'peter.martinez',
  'quinn.robinson',
  'rachel.clark',
  'sam.rodriguez',
  'tina.lewis',
  'uma.lee',
  'victor.walker',
  'wendy.hall',
  'xander.allen',
  'yara.young',
  'zoe.hernandez',
  'aaron.king',
  'bella.wright',
  'carlos.lopez',
  'diana.hill',
  'ethan.scott',
  'fiona.green',
  'george.adams',
  'hannah.baker',
  'ivan.gonzalez',
];

export const MOCK_HUMAN: IdentitySearchRow[] = humanNames.map((name, i) => ({
  identityId: `human-${i + 1}`,
  accountKey: 1000 + i,
  displayName: `${name} (${name
    .replace('.', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())})`,
  email: `${name}@company.com`,
  identityType: 'Human',
  currentTier: tier(i),
  currentUnit: 10 + (i % 90),
  recommendedTier: tier(i + 1),
  recommendedUnit: 5 + (i % 80),
  evaluatedCost: 120 + i * 15,
  evaluationStatus: status(i),
  departmentName: dept(i),
  lastActiveAt: lastActive(i * 3),
  daysInactive: i * 3,
  recommendationReason: i % 3 === 0 ? 'Low activity detected' : undefined,
}));

export const MOCK_SERVICE_ACCOUNT: IdentitySearchRow[] = [
  {
    identityId: 'sa-1',
    accountKey: 2000,
    accountName: 'svc-erp-sync',
    identityType: 'Service Account',
    currentTier: 'Advanced User',
    currentUnit: 55,
    recommendedTier: 'Core User',
    recommendedUnit: 30,
    evaluatedCost: 80,
    evaluationStatus: 'Over-licensed',
    departmentName: 'Engineering',
    lastActiveAt: lastActive(6),
    daysInactive: 6,
  },
  {
    identityId: 'sa-2',
    accountKey: 2001,
    accountName: 'svc-data-pipeline',
    identityType: 'Service Account',
    currentTier: 'Core User',
    currentUnit: 28,
    recommendedTier: 'Core User',
    recommendedUnit: 28,
    evaluatedCost: 90,
    evaluationStatus: 'Compliant',
    departmentName: 'IT',
    lastActiveAt: lastActive(1),
    daysInactive: 1,
  },
];

const emergencyAccountNames = [
  'emrg-admin-access',
  'emrg-db-rescue',
  'emrg-firebreak',
  'emrg-network-repair',
  'emrg-sec-override',
  'emrg-prod-hotfix',
  'emrg-vault-break',
  'emrg-sso-bypass',
  'emrg-audit-review',
  'emrg-incident-mgr',
  'emrg-dr-failover',
  'emrg-cloud-ops',
  'emrg-infra-admin',
  'emrg-domain-admin',
  'emrg-root-access',
  'emrg-backup-restore',
  'emrg-key-rotation',
  'emrg-cert-renewal',
  'emrg-fw-update',
  'emrg-patch-deploy',
  'emrg-data-recover',
  'emrg-acl-override',
  'emrg-svc-restart',
  'emrg-log-purge',
  'emrg-cluster-fix',
  'emrg-token-reset',
  'emrg-ssl-renew',
  'emrg-dns-update',
  'emrg-mfa-bypass',
  'emrg-policy-exec',
  'emrg-queue-flush',
  'emrg-cache-clear',
  'emrg-index-rebuild',
  'emrg-replication',
  'emrg-schema-fix',
];

export const MOCK_EMERGENCY: IdentitySearchRow[] = emergencyAccountNames.map(
  (name, i) => ({
    identityId: `emrg-${i + 1}`,
    accountKey: 3000 + i,
    accountName: name,
    identityType: 'Emergency',
    currentTier: 'Advanced User',
    currentUnit: 90 + (i % 10),
    recommendedTier: 'Advanced User',
    recommendedUnit: 90 + (i % 10),
    evaluatedCost: 300 + i * 5,
    evaluationStatus: status(i),
    departmentName: dept(i),
    lastActiveAt: lastActive(i * 7 + 2),
    daysInactive: i * 7 + 2,
  })
);

const communicationAccountNames = [
  'comm-helpdesk-bot',
  'comm-hr-notify',
  'comm-alert-svc',
  'comm-newsletter',
  'comm-onboarding',
  'comm-offboarding',
  'comm-slack-int',
  'comm-teams-bot',
  'comm-email-digest',
  'comm-sms-gateway',
  'comm-webhook-notify',
  'comm-ticket-bot',
  'comm-survey-sender',
  'comm-report-dist',
  'comm-erp-alerts',
  'comm-crm-emails',
  'comm-it-alerts',
  'comm-sec-alerts',
  'comm-sla-notify',
  'comm-escalation',
  'comm-approval-bot',
  'comm-chatops',
  'comm-voip-agent',
  'comm-paging-svc',
  'comm-backup-notify',
  'comm-deploy-bot',
  'comm-incident-bot',
  'comm-status-page',
  'comm-oauth-notify',
  'comm-cron-alert',
  'comm-compliance',
  'comm-audit-log',
  'comm-digest-daily',
  'comm-digest-weekly',
  'comm-executive',
];

export const MOCK_COMMUNICATION: IdentitySearchRow[] =
  communicationAccountNames.map((name, i) => ({
    identityId: `comm-${i + 1}`,
    accountKey: 4000 + i,
    accountName: name,
    identityType: 'Communication',
    currentTier: tier(i),
    currentUnit: 8 + (i % 50),
    recommendedTier: tier(i + 1),
    recommendedUnit: 4 + (i % 40),
    evaluatedCost: 60 + i * 8,
    evaluationStatus: status(i),
    departmentName: dept(i),
    lastActiveAt: lastActive(i * 2 + 1),
    daysInactive: i * 2 + 1,
  }));

const orphanAccountNames = [
  'orphan-ex-employee-001',
  'orphan-ex-employee-002',
  'orphan-ex-employee-003',
  'orphan-ex-contractor-01',
  'orphan-ex-contractor-02',
  'orphan-terminated-usr1',
  'orphan-terminated-usr2',
  'orphan-old-svc-acct-1',
  'orphan-old-svc-acct-2',
  'orphan-legacy-system-1',
  'orphan-legacy-system-2',
  'orphan-unmapped-acct1',
  'orphan-unmapped-acct2',
  'orphan-deprovisioned-1',
  'orphan-deprovisioned-2',
  'orphan-migration-rem1',
  'orphan-migration-rem2',
  'orphan-test-user-001',
  'orphan-test-user-002',
  'orphan-stale-access-1',
  'orphan-stale-access-2',
  'orphan-no-owner-001',
  'orphan-no-owner-002',
  'orphan-no-owner-003',
  'orphan-vendor-acct-1',
  'orphan-vendor-acct-2',
  'orphan-expired-cred1',
  'orphan-expired-cred2',
  'orphan-unknown-src-1',
  'orphan-unknown-src-2',
  'orphan-abandoned-01',
  'orphan-abandoned-02',
  'orphan-inactive-001',
  'orphan-inactive-002',
  'orphan-dangling-001',
];

export const MOCK_ORPHAN: IdentitySearchRow[] = orphanAccountNames.map(
  (name, i) => ({
    identityId: `orphan-${i + 1}`,
    accountKey: 5000 + i,
    accountName: name,
    identityType: 'Orphan',
    currentTier: tier(i),
    currentUnit: 2 + (i % 30),
    recommendedTier: 'Self-Service User',
    recommendedUnit: 2,
    evaluatedCost: 40 + i * 6,
    evaluationStatus: i % 2 === 0 ? 'Dormant' : 'Over-licensed',
    departmentName: dept(i),
    lastActiveAt: lastActive(30 + i * 4),
    daysInactive: 30 + i * 4,
  })
);

const aiAgentAccountNames = [
  'ai-code-reviewer',
  'ai-test-generator',
  'ai-doc-writer',
  'ai-sec-scanner',
  'ai-perf-advisor',
  'ai-infra-planner',
  'ai-incident-triage',
  'ai-risk-scorer',
  'ai-compliance-chk',
  'ai-anomaly-detect',
  'ai-log-analyzer',
  'ai-cost-optim',
  'ai-user-insights',
  'ai-ticket-router',
  'ai-policy-checker',
  'ai-data-quality',
  'ai-pr-reviewer',
  'ai-deploy-guard',
  'ai-onboard-assist',
  'ai-offboard-chk',
  'ai-access-suggest',
  'ai-role-recomm',
  'ai-siem-analyst',
  'ai-threat-detect',
  'ai-vuln-scanner',
  'ai-chatbot-prod',
  'ai-chatbot-hr',
  'ai-chatbot-it',
  'ai-nlp-extractor',
  'ai-summarizer',
  'ai-classifier',
  'ai-image-tagger',
  'ai-data-pipeline',
  'ai-etl-optimizer',
  'ai-forecast-eng',
];

export const MOCK_AI_AGENT: IdentitySearchRow[] = aiAgentAccountNames.map(
  (name, i) => ({
    identityId: `ai-${i + 1}`,
    accountKey: 6000 + i,
    accountName: name,
    identityType: 'AI Agent',
    currentTier: tier(i),
    currentUnit: 20 + (i % 80),
    recommendedTier: tier(i + 1),
    recommendedUnit: 15 + (i % 70),
    evaluatedCost: 150 + i * 12,
    evaluationStatus: status(i),
    departmentName: dept(i),
    lastActiveAt: lastActive(i * 2),
    daysInactive: i * 2,
    recommendationReason:
      i % 4 === 0 ? 'AI workload pattern detected' : undefined,
  })
);

const batchAccountNames = [
  'batch-nightly-etl',
  'batch-daily-report',
  'batch-weekly-payroll',
  'batch-monthly-audit',
  'batch-user-sync',
  'batch-data-import',
  'batch-data-export',
  'batch-archive-job',
  'batch-cleanup-task',
  'batch-index-rebuild',
  'batch-cache-warm',
  'batch-cert-renew',
  'batch-log-rotate',
  'batch-backup-run',
  'batch-compliance-scan',
  'batch-license-check',
  'batch-access-rev',
  'batch-risk-calc',
  'batch-report-gen',
  'batch-notif-send',
  'batch-db-vacuum',
  'batch-stat-update',
  'batch-feed-ingest',
  'batch-price-update',
  'batch-inv-refresh',
  'batch-reconcile',
  'batch-dedup-run',
  'batch-ml-train',
  'batch-ml-score',
  'batch-forecast-run',
  'batch-acl-sync',
  'batch-group-sync',
  'batch-entitle-sync',
  'batch-provisioner',
  'batch-deprovisioner',
];

export const MOCK_BATCH: IdentitySearchRow[] = batchAccountNames.map(
  (name, i) => ({
    identityId: `batch-${i + 1}`,
    accountKey: 7000 + i,
    accountName: name,
    identityType: 'Batch',
    currentTier: tier(i),
    currentUnit: 15 + (i % 60),
    recommendedTier: tier(i + 2),
    recommendedUnit: 10 + (i % 50),
    evaluatedCost: 100 + i * 9,
    evaluationStatus: status(i),
    departmentName: dept(i),
    lastActiveAt: lastActive(i * 3 + 1),
    daysInactive: i * 3 + 1,
  })
);

export const MOCK_DATA_BY_IDENTITY_TYPE: Record<string, IdentitySearchRow[]> = {
  Human: MOCK_HUMAN,
  'Service Account': MOCK_SERVICE_ACCOUNT,
  Emergency: MOCK_EMERGENCY,
  Communication: MOCK_COMMUNICATION,
  Orphan: MOCK_ORPHAN,
  'AI Agent': MOCK_AI_AGENT,
  Batch: MOCK_BATCH,
};
